import type { ComboboxItem, ComboboxItemGroup } from "@mantine/core";
import { BuiltinPresetCategory as GeneratedBuiltinPresetCategory } from "../../../../../../gql/graphql";

/**
 * Console-facing mirror of the GraphQL `BuiltinPresetCategory` enum with
 * SCREAMING_SNAKE keys (codegen emits PascalCase keys, which would force
 * `BuiltinPresetCategory.SingleElimination` everywhere and mismatch the
 * backend's wire values at a glance).
 *
 * Drift protection: the `categoryEnumIsExhaustive` assignment below fails
 * to compile if this enum's value set diverges from the generated one, so
 * new backend categories must be added here in the same change set.
 *
 * Consumers: `PresetSelector.tsx` (templates tab) and `PresetScriptForm.tsx`
 * (start-from-template picker).
 */
export enum BuiltinPresetCategory {
  SINGLE_ELIMINATION = "SINGLE_ELIMINATION",
  DOUBLE_ELIMINATION = "DOUBLE_ELIMINATION",
  SWISS = "SWISS",
  ROUND_ROBIN = "ROUND_ROBIN",
  GROUP_STAGE = "GROUP_STAGE",
  OTHER = "OTHER",
}

/**
 * Compile-time guard: both enums must have the exact same set of string
 * values. If the backend adds/removes a category and codegen runs, one of
 * the two assertions below fails to compile until the local enum and the
 * maps are updated.
 */
type LocalCoversGenerated =
  `${GeneratedBuiltinPresetCategory}` extends `${BuiltinPresetCategory}`
    ? true
    : never;
type GeneratedCoversLocal =
  `${BuiltinPresetCategory}` extends `${GeneratedBuiltinPresetCategory}`
    ? true
    : never;
const _localCoversGenerated: LocalCoversGenerated = true;
const _generatedCoversLocal: GeneratedCoversLocal = true;
void _localCoversGenerated;
void _generatedCoversLocal;

export const BUILTIN_PRESET_CATEGORY_LABELS: Record<
  BuiltinPresetCategory,
  string
> = {
  [BuiltinPresetCategory.SINGLE_ELIMINATION]: "Single elimination",
  [BuiltinPresetCategory.DOUBLE_ELIMINATION]: "Double elimination",
  [BuiltinPresetCategory.SWISS]: "Swiss",
  [BuiltinPresetCategory.ROUND_ROBIN]: "Round robin",
  [BuiltinPresetCategory.GROUP_STAGE]: "Group stage",
  [BuiltinPresetCategory.OTHER]: "Other formats",
};

/**
 * Display order for the templates picker. Use this whenever you iterate
 * categories in UI so they appear in a sensible family-grouped sequence.
 *
 * Typed as a length-bounded tuple so adding a new enum member without
 * extending this tuple is a compile error (prevents silent omission in
 * the grouped UI).
 */
export const BUILTIN_PRESET_CATEGORY_ORDER: readonly [
  BuiltinPresetCategory,
  BuiltinPresetCategory,
  BuiltinPresetCategory,
  BuiltinPresetCategory,
  BuiltinPresetCategory,
  BuiltinPresetCategory,
] = [
  BuiltinPresetCategory.SINGLE_ELIMINATION,
  BuiltinPresetCategory.DOUBLE_ELIMINATION,
  BuiltinPresetCategory.SWISS,
  BuiltinPresetCategory.ROUND_ROBIN,
  BuiltinPresetCategory.GROUP_STAGE,
  BuiltinPresetCategory.OTHER,
];

/**
 * Minimal preset shape required by `groupPresetsForSelect`. Kept narrow on
 * purpose so any caller (list queries, cached fragments, fixtures) can pass
 * its summary type directly via structural typing — no coupling to the full
 * GraphQL result shape.
 */
export interface BuiltinPresetSummaryForSelect {
  readonly id: string;
  readonly displayName: string;
  readonly category: BuiltinPresetCategory;
}

/**
 * Groups presets into Mantine Select `ComboboxItemGroup`s following
 * `BUILTIN_PRESET_CATEGORY_ORDER` and drops empty groups. Single pass over
 * the input: seed a Map with empty buckets in display order, bucket each
 * preset once, then project to the grouped shape.
 */
export const groupPresetsForSelect = (
  presets: readonly BuiltinPresetSummaryForSelect[],
): ComboboxItemGroup<ComboboxItem>[] => {
  const buckets = new Map<BuiltinPresetCategory, ComboboxItem[]>(
    BUILTIN_PRESET_CATEGORY_ORDER.map((category) => [category, []]),
  );
  for (const preset of presets) {
    buckets
      .get(preset.category)
      ?.push({ value: preset.id, label: preset.displayName });
  }
  return BUILTIN_PRESET_CATEGORY_ORDER.flatMap((category) => {
    const items = buckets.get(category) ?? [];
    return items.length > 0
      ? [{ group: BUILTIN_PRESET_CATEGORY_LABELS[category], items }]
      : [];
  });
};
